import * as THREE from 'three';
import { createSeededRandom } from '../utils/random';

/**
 * Ambient underwater life: a few sea turtles and a handful of darting fish
 * schools that drift below the ocean surface around the shared ship.
 *
 * Everything lives in absolute world space (the ocean surface is y = 0), so
 * creatures stay underwater while the ship heaves on the swell. The ship's XZ
 * position is passed into `update` as the loose orbit/leash centre.
 *
 * Visibility: the creatures are drawn with a negative renderOrder and slightly
 * translucent materials, and the `Water` surface is configured to blend over
 * them (see the `OceanSystem` wiring in `Game`), so they read as underwater
 * shapes tinted by the sea rather than popping through the surface.
 */

/** Draw underwater props before the translucent water plane. */
const RENDER_ORDER = -1;

// --- Sea turtles ---------------------------------------------------------
const TURTLE_MIN = 2;
const TURTLE_MAX = 3;
const TURTLE_RADIUS_MIN = 20;
const TURTLE_RADIUS_MAX = 60;
/** Depth range below the surface (negative Y). */
const TURTLE_DEPTH_MIN = -8;
const TURTLE_DEPTH_MAX = -3;
const TURTLE_SPEED_MIN = 1.0;
const TURTLE_SPEED_MAX = 1.9;
/** Y the turtle rises to when it breaks the surface for air. */
const TURTLE_SURFACE_Y = -0.5;
const TURTLE_SURFACE_DURATION_MIN = 4;
const TURTLE_SURFACE_DURATION_MAX = 7;
const TURTLE_SURFACE_INTERVAL_MIN = 10;
const TURTLE_SURFACE_INTERVAL_MAX = 28;

// --- Fish schools --------------------------------------------------------
const SCHOOL_MIN = 2;
const SCHOOL_MAX = 3;
const FISH_MIN = 8;
const FISH_MAX = 15;
/** Depth range below the surface (negative Y). */
const SCHOOL_DEPTH_MIN = -6;
const SCHOOL_DEPTH_MAX = -2;
const SCHOOL_SPEED_MIN = 3.0;
const SCHOOL_SPEED_MAX = 5.5;
/** Distance from the ship past which a school turns back toward it. */
const SCHOOL_LEASH = 68;
/** Distance from the hull that triggers the scatter/regroup response. */
const SCHOOL_SCATTER_RADIUS = 11;
/** Seconds for a scattered school to fully regroup. */
const SCHOOL_REGROUP_TIME = 2.4;

interface TurtleFlipper {
  readonly pivot: THREE.Group;
  /** +1 for the starboard flipper, -1 for port. */
  readonly side: number;
  readonly amplitude: number;
  readonly speed: number;
  readonly phase: number;
}

interface Turtle {
  readonly group: THREE.Group;
  readonly tilt: THREE.Group;
  readonly flippers: TurtleFlipper[];
  readonly radius: number;
  readonly angularSpeed: number;
  /** Base swim depth (negative Y). */
  readonly depth: number;
  readonly bobAmplitude: number;
  readonly bobSpeed: number;
  readonly bobPhase: number;
  readonly paddleSpeed: number;
  readonly paddlePhase: number;
  readonly surfaceDuration: number;
  readonly surfaceInterval: number;
  angle: number;
  surfaceTimer: number;
  surfacing: boolean;
  surfaceProgress: number;
}

interface FishMember {
  /** Formation offset from the school leader. */
  readonly offset: THREE.Vector3;
  readonly phase: number;
  readonly yaw: number;
  readonly scale: number;
}

interface School {
  readonly leader: THREE.Vector3;
  readonly instance: THREE.InstancedMesh;
  readonly members: FishMember[];
  /** Base swim depth (negative Y). */
  readonly depth: number;
  readonly bobPhase: number;
  heading: number;
  targetHeading: number;
  speed: number;
  targetSpeed: number;
  turnTimer: number;
  /** 1 = fully scattered, 0 = tight formation. */
  scatter: number;
}

export class SeaLife {
  private readonly group = new THREE.Group();
  private readonly turtles: Turtle[] = [];
  private readonly schools: School[] = [];
  private readonly geometries: THREE.BufferGeometry[] = [];
  private readonly materials: THREE.Material[] = [];
  private readonly rng: () => number;
  private readonly dummy = new THREE.Object3D();

  constructor(scene: THREE.Scene) {
    this.group.name = 'sea-life';
    scene.add(this.group);

    this.rng = createSeededRandom(90210);
    this.buildTurtles();
    this.buildSchools();

    // Draw every underwater prop before the water so the surface tints it.
    this.group.traverse((object) => {
      object.renderOrder = RENDER_ORDER;
    });
  }

  /**
   * Animate every creature.
   * @param delta  Frame delta in seconds.
   * @param elapsed Elapsed animation time in seconds.
   * @param shipX  Ship world X (loose orbit / leash centre).
   * @param shipZ  Ship world Z (loose orbit / leash centre).
   */
  update(delta: number, elapsed: number, shipX: number, shipZ: number): void {
    for (const turtle of this.turtles) {
      this.updateTurtle(turtle, delta, elapsed, shipX, shipZ);
    }
    for (const school of this.schools) {
      this.updateSchool(school, delta, elapsed, shipX, shipZ);
    }
  }

  dispose(): void {
    for (const school of this.schools) {
      school.instance.dispose();
    }
    this.group.removeFromParent();
    for (const geometry of this.geometries) geometry.dispose();
    for (const material of this.materials) material.dispose();
    this.turtles.length = 0;
    this.schools.length = 0;
  }

  // --- construction ------------------------------------------------------

  private buildTurtles(): void {
    const shellGeometry = this.geometry(new THREE.SphereGeometry(0.6, 12, 9));
    const headGeometry = this.geometry(new THREE.SphereGeometry(0.2, 8, 7));
    const tailGeometry = this.geometry(new THREE.ConeGeometry(0.12, 0.35, 6));
    const flipperGeometry = this.geometry(new THREE.SphereGeometry(0.3, 8, 6));

    const shellMaterial = this.material(
      new THREE.MeshStandardMaterial({
        color: 0x33502f,
        roughness: 0.62,
        metalness: 0.05,
        emissive: 0x16220f,
        emissiveIntensity: 0.55,
        flatShading: true,
        transparent: true,
        opacity: 0.94,
      }),
    );
    const skinMaterial = this.material(
      new THREE.MeshStandardMaterial({
        color: 0x4f3d26,
        roughness: 0.7,
        metalness: 0.04,
        emissive: 0x1d1710,
        emissiveIntensity: 0.5,
        flatShading: true,
        transparent: true,
        opacity: 0.94,
      }),
    );

    const count = TURTLE_MIN + Math.floor(this.rng() * (TURTLE_MAX - TURTLE_MIN + 1));
    for (let i = 0; i < count; i += 1) {
      const group = new THREE.Group();
      const tilt = new THREE.Group();
      group.add(tilt);

      // Flattened oval shell. Forward is local +Z.
      const shell = new THREE.Mesh(shellGeometry, shellMaterial);
      shell.scale.set(1.35, 0.42, 1.0);
      shell.position.y = 0.05;
      tilt.add(shell);

      const head = new THREE.Mesh(headGeometry, skinMaterial);
      head.scale.set(0.9, 0.9, 1.1);
      head.position.set(0, 0.02, 0.72);
      tilt.add(head);

      const tail = new THREE.Mesh(tailGeometry, skinMaterial);
      tail.rotation.x = -Math.PI / 2;
      tail.position.set(0, 0.02, -0.72);
      tilt.add(tail);

      // Four flat paddle flippers: bigger fronts, smaller rears.
      const specs = [
        { x: 0.42, z: 0.5, side: 1, amplitude: 0.55, speed: 1, phase: 0 },
        { x: -0.42, z: 0.5, side: -1, amplitude: 0.55, speed: 1, phase: 0 },
        { x: 0.38, z: -0.45, side: 1, amplitude: 0.32, speed: 0.8, phase: Math.PI * 0.6 },
        { x: -0.38, z: -0.45, side: -1, amplitude: 0.32, speed: 0.8, phase: Math.PI * 0.6 },
      ];
      const flippers: TurtleFlipper[] = [];
      for (const spec of specs) {
        const pivot = new THREE.Group();
        pivot.position.set(spec.x, 0, spec.z);
        tilt.add(pivot);

        const paddle = new THREE.Mesh(flipperGeometry, skinMaterial);
        paddle.scale.set(1.5, 0.12, 0.55);
        paddle.position.set(spec.side * 0.5, 0, 0);
        pivot.add(paddle);

        flippers.push({
          pivot,
          side: spec.side,
          amplitude: spec.amplitude,
          speed: spec.speed,
          phase: spec.phase,
        });
      }

      const radius = this.range(TURTLE_RADIUS_MIN, TURTLE_RADIUS_MAX);
      const speed = this.range(TURTLE_SPEED_MIN, TURTLE_SPEED_MAX);
      const direction = this.rng() < 0.5 ? -1 : 1;

      this.turtles.push({
        group,
        tilt,
        flippers,
        radius,
        angularSpeed: (speed / radius) * direction,
        depth: this.range(TURTLE_DEPTH_MIN, TURTLE_DEPTH_MAX),
        bobAmplitude: this.range(0.25, 0.6),
        bobSpeed: this.range(0.25, 0.5),
        bobPhase: this.rng() * Math.PI * 2,
        paddleSpeed: this.range(0.55, 0.95),
        paddlePhase: this.rng() * Math.PI * 2,
        surfaceDuration: this.range(TURTLE_SURFACE_DURATION_MIN, TURTLE_SURFACE_DURATION_MAX),
        surfaceInterval: this.range(TURTLE_SURFACE_INTERVAL_MIN, TURTLE_SURFACE_INTERVAL_MAX),
        angle: this.rng() * Math.PI * 2,
        surfaceTimer: this.range(TURTLE_SURFACE_INTERVAL_MIN, TURTLE_SURFACE_INTERVAL_MAX),
        surfacing: false,
        surfaceProgress: 0,
      });

      this.group.add(group);
    }
  }

  private buildSchools(): void {
    // Elongated cone rotated so the tip points along local +Z (forward).
    const fishGeometry = this.geometry(new THREE.ConeGeometry(0.055, 0.26, 5));
    fishGeometry.rotateX(Math.PI / 2);
    fishGeometry.scale(0.85, 0.85, 1);

    const fishMaterial = this.material(
      new THREE.MeshStandardMaterial({
        color: 0xaec4dd,
        roughness: 0.35,
        metalness: 0.55,
        emissive: 0x244a6e,
        emissiveIntensity: 0.6,
        flatShading: true,
        transparent: true,
        opacity: 0.92,
        depthWrite: false,
      }),
    );

    const count = SCHOOL_MIN + Math.floor(this.rng() * (SCHOOL_MAX - SCHOOL_MIN + 1));
    for (let s = 0; s < count; s += 1) {
      const memberCount = FISH_MIN + Math.floor(this.rng() * (FISH_MAX - FISH_MIN + 1));
      const members: FishMember[] = [];
      for (let i = 0; i < memberCount; i += 1) {
        members.push({
          offset: new THREE.Vector3(
            this.range(-3, 3),
            this.range(-0.8, 0.8),
            this.range(-3, 3),
          ),
          phase: this.rng() * Math.PI * 2,
          yaw: this.range(-0.3, 0.3),
          scale: this.range(0.8, 1.25),
        });
      }

      const instance = new THREE.InstancedMesh(fishGeometry, fishMaterial, memberCount);
      instance.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      instance.frustumCulled = false;
      instance.castShadow = false;
      instance.receiveShadow = false;
      this.group.add(instance);

      const angle = this.rng() * Math.PI * 2;
      const radius = this.range(25, 55);
      const depth = this.range(SCHOOL_DEPTH_MIN, SCHOOL_DEPTH_MAX);
      const speed = this.range(SCHOOL_SPEED_MIN, SCHOOL_SPEED_MAX);

      this.schools.push({
        leader: new THREE.Vector3(Math.cos(angle) * radius, depth, Math.sin(angle) * radius),
        instance,
        members,
        depth,
        bobPhase: this.rng() * Math.PI * 2,
        heading: angle + Math.PI / 2,
        targetHeading: angle + Math.PI / 2,
        speed,
        targetSpeed: speed,
        turnTimer: this.range(1.4, 3.6),
        scatter: 0,
      });
    }
  }

  // --- animation ---------------------------------------------------------

  private updateTurtle(
    turtle: Turtle,
    delta: number,
    elapsed: number,
    shipX: number,
    shipZ: number,
  ): void {
    turtle.angle += turtle.angularSpeed * delta;

    // Air-breathing cycle: cruise, rise for a breath, then dive back down.
    if (!turtle.surfacing) {
      turtle.surfaceTimer -= delta;
      if (turtle.surfaceTimer <= 0) {
        turtle.surfacing = true;
        turtle.surfaceProgress = 0;
      }
    } else {
      turtle.surfaceProgress += delta / turtle.surfaceDuration;
      if (turtle.surfaceProgress >= 1) {
        turtle.surfacing = false;
        turtle.surfaceProgress = 0;
        turtle.surfaceTimer = this.range(TURTLE_SURFACE_INTERVAL_MIN, TURTLE_SURFACE_INTERVAL_MAX);
      }
    }
    const surfaceArc = turtle.surfacing
      ? Math.sin(Math.PI * Math.min(turtle.surfaceProgress, 1))
      : 0;

    // Loose orbit: the radius breathes a little so the path is never a circle.
    const radius = turtle.radius + Math.sin(elapsed * 0.11 + turtle.bobPhase) * 4;
    const cos = Math.cos(turtle.angle);
    const sin = Math.sin(turtle.angle);
    const x = shipX + radius * cos;
    const z = shipZ + radius * sin;
    const y =
      turtle.depth +
      Math.sin(elapsed * turtle.bobSpeed + turtle.bobPhase) * turtle.bobAmplitude +
      surfaceArc * (TURTLE_SURFACE_Y - turtle.depth);

    turtle.group.position.set(x, y, z);

    // Face the tangent of the orbit (forward is local +Z).
    const direction = Math.sign(turtle.angularSpeed) || 1;
    turtle.group.rotation.y = Math.atan2(-sin * direction, cos * direction);

    // Nose up while surfacing, gentle bank into the turn.
    turtle.tilt.rotation.x =
      -surfaceArc * 0.28 + Math.sin(elapsed * 0.35 + turtle.bobPhase) * 0.05;
    turtle.tilt.rotation.z = -direction * 0.12;

    for (const flipper of turtle.flippers) {
      const wave = Math.sin(elapsed * turtle.paddleSpeed * flipper.speed + flipper.phase);
      flipper.pivot.rotation.z = flipper.side * wave * flipper.amplitude;
      flipper.pivot.rotation.x = wave * 0.12;
    }
  }

  private updateSchool(
    school: School,
    delta: number,
    elapsed: number,
    shipX: number,
    shipZ: number,
  ): void {
    const dx = shipX - school.leader.x;
    const dz = shipZ - school.leader.z;
    const distance = Math.hypot(dx, dz);

    // Near the hull the school breaks formation and darts away, then regroups.
    if (distance < SCHOOL_SCATTER_RADIUS) {
      if (school.scatter < 0.3) {
        school.targetHeading = Math.atan2(-dx, -dz);
        school.targetSpeed = SCHOOL_SPEED_MAX * 1.35;
      }
      school.scatter = 1;
    }

    school.scatter = Math.max(0, school.scatter - delta / SCHOOL_REGROUP_TIME);

    school.turnTimer -= delta;
    if (school.turnTimer <= 0) {
      school.targetHeading = school.heading + this.range(-1.4, 1.4);
      school.targetSpeed = this.range(SCHOOL_SPEED_MIN, SCHOOL_SPEED_MAX);
      school.turnTimer = this.range(1.4, 3.6);
    }

    // The leash beats the random wander: steer back toward the ship so ambient
    // life stays in frame instead of drifting off the map.
    if (distance > SCHOOL_LEASH) {
      school.targetHeading = Math.atan2(dx, dz);
    }

    const turnRate = 1.2 + school.scatter * 3;
    school.heading +=
      shortestAngle(school.heading, school.targetHeading) * Math.min(1, delta * turnRate);
    school.speed +=
      (school.targetSpeed * (1 + school.scatter * 0.4) - school.speed) *
      Math.min(1, delta * 2.5);

    school.leader.x += Math.sin(school.heading) * school.speed * delta;
    school.leader.z += Math.cos(school.heading) * school.speed * delta;

    const targetDepth =
      school.depth + Math.sin(elapsed * 0.7 + school.bobPhase) * 0.6 - school.scatter * 0.6;
    school.leader.y += (targetDepth - school.leader.y) * Math.min(1, delta * 2);

    const cos = Math.cos(school.heading);
    const sin = Math.sin(school.heading);
    const spread = 1 + school.scatter * 2.4;

    for (let i = 0; i < school.members.length; i += 1) {
      const member = school.members[i];
      const offsetX = member.offset.x * spread;
      const offsetZ = member.offset.z * spread;
      const wiggle = Math.sin(elapsed * 6 + member.phase);

      this.dummy.position.set(
        school.leader.x + offsetX * cos + offsetZ * sin,
        school.leader.y + member.offset.y * spread + Math.sin(elapsed * 4.5 + member.phase) * 0.12,
        school.leader.z - offsetX * sin + offsetZ * cos,
      );
      this.dummy.rotation.set(
        Math.sin(elapsed * 7 + member.phase) * 0.18,
        school.heading + member.yaw + wiggle * 0.12,
        school.scatter * Math.sin(member.phase) * 0.5,
      );
      this.dummy.scale.setScalar(member.scale);
      this.dummy.updateMatrix();
      school.instance.setMatrixAt(i, this.dummy.matrix);
    }
    school.instance.instanceMatrix.needsUpdate = true;
  }

  // --- helpers -----------------------------------------------------------

  private range(min: number, max: number): number {
    return min + this.rng() * (max - min);
  }

  private geometry<T extends THREE.BufferGeometry>(geometry: T): T {
    this.geometries.push(geometry);
    return geometry;
  }

  private material<T extends THREE.Material>(material: T): T {
    this.materials.push(material);
    return material;
  }
}

/** Shortest signed angular distance from `from` to `to`. */
function shortestAngle(from: number, to: number): number {
  let delta = (to - from) % (Math.PI * 2);
  if (delta > Math.PI) delta -= Math.PI * 2;
  if (delta < -Math.PI) delta += Math.PI * 2;
  return delta;
}
